import data from "../../server/strength.json";

export const levels = ["Beginner", "Novice", "Intermediate", "Advanced", "Elite"];
export const levelLabel = (level: number) => (level < 0 ? "Untrained" : levels[level]);
export const percentiles = [5, 20, 50, 80, 95];

type Table = {bodyweight: number[][]; age: number[][]; community: number[]};
type Standard = {
    group: string;
    source: string;
    male: Table & {ratio: number[]; weighted?: Table};
    female: Table & {ratio: number[]; weighted?: Table};
};

export const standards: Partial<Record<string, Standard>> = data.exercises;
export const kgPerLb = 0.45359237;
export const interpolate = (rows: number[][], value: number) => {
    const upper = rows.findIndex((row) => row[0] >= value);
    if (upper === 0) return rows[0].slice(1);
    if (upper === -1) return rows[rows.length - 1].slice(1);
    const before = rows[upper - 1];
    const after = rows[upper];
    const fraction = (value - before[0]) / (after[0] - before[0]);
    return before.slice(1).map((v, i) => v + (after[i + 1] - v) * fraction);
};

export const thresholds = (
    table: Table,
    weight: number,
    age: number,
    weighted: boolean,
) => {
    const byWeight = interpolate(table.bodyweight, weight);
    const byAge = interpolate(table.age, age);
    const peak = interpolate(table.age, 30);
    return byWeight.map((value, i) => {
        // Added-load tables can be negative; age scaling applies to total system mass.
        const offset = weighted ? weight : 0;
        return (value + offset) * ((byAge[i] + offset) / (peak[i] + offset)) - offset;
    });
};

export const ranking = (value: number, values: number[]) => {
    const index = values.findIndex((v) => value < v);
    const level = index === -1 ? 4 : index - 1;
    const percentile =
        index === -1
            ? 95
            : index === 0
              ? Math.max(0, (5 * value) / Math.max(values[0], 0.5))
              : percentiles[index - 1] +
                ((percentiles[index] - percentiles[index - 1]) *
                    (value - values[index - 1])) /
                    (values[index] - values[index - 1]);
    return {level, percentile: Math.round(percentile)};
};

export const oneRepMax = (weight: number, reps: number) =>
    reps === 1 ? weight : weight * (1 + reps / 30);

export const strengthLifts: Record<string, {group: string}> = data.exercises;

export const strengthMethod = `
## How this is calculated

### Estimated one-rep max

For sets of two or more reps, we use Epley:

**1RM = weight × (1 + reps ÷ 30)**

A single rep uses the weight you entered. Estimates become less reliable above 10 reps. Include the bar in the lifted weight.

### Strength level

We use published [Strength Level tables](https://strengthlevel.com/strength-standards), retrieved September 6, 2026, for each exercise and sex. We interpolate between bodyweights and adjust each threshold using the published age tables relative to age 30.

- **Beginner:** 5th percentile
- **Novice:** 20th percentile
- **Intermediate:** 50th percentile
- **Advanced:** 80th percentile
- **Elite:** 95th percentile

Percentiles compare you with other lifters. We interpolate between these boundaries and cap the result at 95%. Our estimates may differ from Strength Level’s calculator. We do not extrapolate outside the published bodyweight or age range.

### Bodyweight ratio

Your estimated one-rep max divided by your bodyweight.

### DOTS and Wilks

Both scores adjust lifted weight for bodyweight and sex. DOTS uses the same calculation as Joy’s meet results. Wilks uses the 2020 coefficients.

Here, both scores use **this lift’s estimated one-rep max**, not a three-lift competition total. Age does not change these scores. The formulas clamp bodyweight to their supported ranges.

### Rounding

Derived weights and scores display as whole numbers; ratios use one decimal. Calculations retain their original precision when you switch units.
`.trim();
